import { makeStyles } from '@material-ui/core';
import { memo, useRef, useCallback } from 'react';
import classNames from 'classnames';
import moment from 'moment';
import { statusColor, formattedDateRange } from 'components/Home/Forecast/utils';
import { CalendarItemType } from './Calendar';
import { useMediaQuery } from '@material-ui/core';
import { getCalendarItemDisplayProps, getActualItemDayIndices } from './utils/calendarPositioning';
import { useClickDragDetection } from './hooks/useClickDragDetection';
import ForecastTooltip from 'components/shared/ForecastTooltip';
import { haptic } from 'utils/haptics';

// Stronger background colors for calendar items (higher opacity than the global statusBackgroundColor)
const calendarBackgroundColor = (status: string | undefined) => {
  switch (status) {
    case '0': return 'rgba(64, 166, 218, 0.32)';   // blue - Available
    case '1': return 'rgba(101, 200, 152, 0.32)';   // green - Some availability
    case '2': return 'rgba(241, 208, 108, 0.32)';   // yellow - Busy
    case '3': return 'rgba(251, 137, 137, 0.32)';   // red - Very busy
    default: return 'rgba(56, 86, 107, 0.25)';     // blueGray - Away/other
  }
};

const useStyles = () => makeStyles(() => ({
  calendar_item: {
    position: 'absolute',
    height: '45px',
    '@media (max-width: 600px)': {
      height: '50px',
    },
    marginTop: 0,
    userSelect: 'none',
    overflow: 'hidden',
    border: '0.5px solid white',
    display: 'flex',
    alignItems: 'center',
    zIndex: 2,
    '&:hover': {
      zIndex: 3,
    },
  },
  calendar_item_start_end: {
    borderRadius: '12px',
  },
  calendar_item_start: {
    borderRadius: '12px 0 0 12px',
  },
  calendar_item_end: {
    borderRadius: '0 12px 12px 0',
  },
  calendar_item_editable: {
    cursor: 'pointer',
    transition: 'box-shadow 0.15s ease-in-out, transform 0.15s ease-in-out',
    '&:hover': {
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
      transform: 'translateY(-2px)',
      zIndex: 5,
    },
  },
  content: {
    padding: '4px 6px',
    height: '100%',
    width: '100%',
    borderRadius: 'inherit',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    gap: '6px',
    overflow: 'hidden',
  },
  statusSection: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    flex: '0 0 auto',
    overflow: 'hidden',
  },
  title: {
    fontSize: 11,
    fontWeight: 600,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  dateRange: {
    fontSize: 9,
    fontWeight: 400,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    opacity: 0.8,
  },
  commentSection: {
    backgroundColor: '#FFFFFF',
    borderRadius: '6px',
    padding: '2px 6px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flex: '0 1 auto',
    maxHeight: '40px',
    position: 'relative',
    '&::after': {
      content: '""',
      position: 'absolute',
      bottom: -5,
      left: 0,
      width: 0,
      height: 0,
      borderTop: '9px solid #FFFFFF',
      borderRight: '6px solid transparent',
    },
  },
  commentText: {
    fontSize: 11,
    fontWeight: 500,
    color: '#333333',
    overflow: 'hidden',
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    textOverflow: 'ellipsis',
    lineHeight: '1.3',
    wordBreak: 'break-word',
  },
  commentIcon: {
    position: 'absolute',
    right: 4,
    top: '50%',
    transform: 'translateY(-50%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  resizeHandle: {
    position: 'absolute',
    top: 0,
    bottom: 0,
    width: '24px',
    cursor: 'ew-resize',
    zIndex: 1,
    display: 'flex',
    alignItems: 'center',
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.2)',
    },
  },
  resizeHandleLeft: {
    left: 0,
    justifyContent: 'flex-start',
    paddingLeft: 2.2,
  },
  resizeHandleRight: {
    right: 0,
    justifyContent: 'flex-end',
    paddingRight: 3,
  },
  resizeGrip: {
    width: 2,
    height: '50%',
    borderRadius: 1.5,
    backgroundColor: 'rgba(255, 255, 255, 0.60)',
  },
}));

const CalendarItem = ({
  properties,
  user,
  isEditable,
  edit,
  startDate,
  length,
  dayPixelWidth,
  onMouseDownMove,
  onMouseDownResize,
  onTouchMove: onTouchMoveItem,
  onTouchResize,
  isBeingDragged,
}: {
  properties: CalendarItemType;
  user: { name: string | null | undefined; title: string | null | undefined; profilePicture: string | null | undefined };
  isEditable: boolean;
  edit: () => void;
  startDate: moment.Moment;
  length: number;
  dayPixelWidth: number;
  onMouseDownMove?: (e: React.MouseEvent, itemId: string, startDay: number, endDay: number) => void;
  onMouseDownResize?: (e: React.MouseEvent, itemId: string, startDay: number, endDay: number, resizeEdge: 'start' | 'end') => void;
  onTouchMove?: (clientX: number, itemId: string, startDay: number, endDay: number) => void;
  onTouchResize?: (clientX: number, itemId: string, startDay: number, endDay: number, resizeEdge: 'start' | 'end') => void;
  isBeingDragged?: boolean;
}) => {
  // ALL HOOKS MUST BE CALLED AT THE TOP - Rules of Hooks
  const isMobile = useMediaQuery('(max-width:600px)');
  const isSmallMobile = useMediaQuery('(max-width:400px)');

  // Calculate item display properties
  const displayProps = getCalendarItemDisplayProps(
    properties.startDate,
    properties.endDate,
    startDate,
    length,
    {
      calendar_item_start_end: '',
      calendar_item_start: '',
      calendar_item_end: '',
    }
  );

  const itemLength = displayProps.isVisible ? displayProps.itemLength : 0;
  const classes = useStyles()();

  // Calculate actual (unclamped) day indices for drag operations
  const { actualStartDay, actualEndDay } = getActualItemDayIndices(
    properties.startDate,
    properties.endDate,
    startDate
  );

  // Set up drag detection - must be called before any early returns
  const { handleMouseDown, handleClick } = useClickDragDetection({
    onDragStart: (e) => {
      if (onMouseDownMove) {
        onMouseDownMove(e, properties._id || '', actualStartDay, actualEndDay);
      }
    },
    onEditClick: edit,
    dragThreshold: 5
  });

  // Touch drag-to-move on mobile: horizontal swipe OR long-press (~1s)
  // Uses touch-action: pan-y so browser handles vertical scroll, JS handles horizontal
  const moveTouchStart = useRef<{ x: number; y: number } | null>(null);
  const moveDragStarted = useRef(false);
  const itemTouchActive = useRef(false);
  const longPressTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleItemTouchStart = useCallback((e: React.TouchEvent) => {
    if (!onTouchMoveItem) return;
    e.stopPropagation();
    itemTouchActive.current = true;
    moveDragStarted.current = false;
    const touch = e.touches[0];
    moveTouchStart.current = { x: touch.clientX, y: touch.clientY };

    // Long-press fallback: after 1s of holding, start move drag
    longPressTimer.current = setTimeout(() => {
      if (moveTouchStart.current && !moveDragStarted.current) {
        moveDragStarted.current = true;
        haptic('medium');
        onTouchMoveItem(moveTouchStart.current.x, properties._id || '', actualStartDay, actualEndDay);
      }
    }, 500);
  }, [onTouchMoveItem, properties._id, actualStartDay, actualEndDay]);

  const handleItemTouchMove = useCallback((e: React.TouchEvent) => {
    if (!moveTouchStart.current || moveDragStarted.current) return;
    const touch = e.touches[0];
    const dx = Math.abs(touch.clientX - moveTouchStart.current.x);
    const dy = Math.abs(touch.clientY - moveTouchStart.current.y);
    if (dx - dy > 3) {
      // Horizontal movement detected — start the move drag
      if (longPressTimer.current) { clearTimeout(longPressTimer.current); longPressTimer.current = null; }
      moveDragStarted.current = true;
      haptic('medium');
      onTouchMoveItem?.(touch.clientX, properties._id || '', actualStartDay, actualEndDay);
    } else if (dy > 15) {
      // Clearly scrolling vertically — cancel long-press
      if (longPressTimer.current) { clearTimeout(longPressTimer.current); longPressTimer.current = null; }
    }
  }, [onTouchMoveItem, properties._id, actualStartDay, actualEndDay]);

  const handleItemTouchEnd = useCallback((e: React.TouchEvent) => {
    // Clear long-press timer
    if (longPressTimer.current) { clearTimeout(longPressTimer.current); longPressTimer.current = null; }
    // Only handle if this item's own touchstart fired (not from resize handle bubbling)
    if (!itemTouchActive.current) return;
    itemTouchActive.current = false;
    // If drag wasn't started, only edit if finger barely moved (tap, not scroll)
    if (!moveDragStarted.current && moveTouchStart.current) {
      const touch = e.changedTouches[0];
      const dy = Math.abs(touch.clientY - moveTouchStart.current.y);
      if (dy < 10) {
        edit();
      }
    }
    moveDragStarted.current = false;
    moveTouchStart.current = null;
  }, [edit]);

  // Early return AFTER all hooks have been called
  if (!displayProps.isVisible) return null;

  const { itemStyle } = displayProps;

  const backgroundColor = calendarBackgroundColor(properties.status);
  const textColor = statusColor(properties.status);

  // Determine what to show based on pixel width and whether there's a comment
  const hasComment = Boolean(properties.comment);
  const itemPixelWidth = itemLength * dayPixelWidth;
  const isLarge = itemPixelWidth > 150;  // room for both status text and comment
  const isMedium = itemPixelWidth > 35;  // room for comment text only
  let commentFontSize = itemPixelWidth < 60 ? 9 : itemPixelWidth < 100 ? 10 : 11;
  const commentLength = properties.comment?.length || 0;
  if (itemPixelWidth < 60 && commentLength > 20) commentFontSize -= 1;
  if (itemPixelWidth < 52 && commentLength > 30) commentFontSize -= 1;

  let showStatusText = false;
  let showCommentText = false;
  let showCommentIcon = false;

  if (isSmallMobile) {
    showCommentIcon = hasComment;
  } else if (hasComment) {
    if (isLarge) {
      showStatusText = true;
      showCommentText = true;
    } else if (isMedium) {
      showCommentText = true;
    } else {
      showStatusText = true;
      showCommentIcon = true;
    }
  } else {
    showStatusText = true;
  }

  // Recalculate border style with actual classes
  const finalDisplayProps = getCalendarItemDisplayProps(
    properties.startDate,
    properties.endDate,
    startDate,
    length,
    {
      calendar_item_start_end: classes.calendar_item_start_end,
      calendar_item_start: classes.calendar_item_start,
      calendar_item_end: classes.calendar_item_end,
    }
  );

  const borderStyle = finalDisplayProps.isVisible ? finalDisplayProps.borderStyle : '';

  return (
    <div
      className={classNames(classes.calendar_item, 'calendar-item', borderStyle, {
        [classes.calendar_item_editable]: isEditable,
      })}
      style={{
        ...itemStyle,
        background: backgroundColor,
        color: textColor,
        opacity: isBeingDragged ? 0.5 : 1,
        pointerEvents: isBeingDragged ? 'none' : 'auto',
        touchAction: onTouchMoveItem ? 'pan-y' : undefined,
      }}
      onClick={isEditable ? handleClick : undefined}
      onMouseDown={isEditable && onMouseDownMove ? handleMouseDown : undefined}
      onTouchStart={isEditable && onTouchMoveItem ? handleItemTouchStart : undefined}
      onTouchMove={isEditable && onTouchMoveItem ? handleItemTouchMove : undefined}
      onTouchEnd={isEditable && onTouchMoveItem ? handleItemTouchEnd : undefined}
      title={``}
    >
      <ForecastTooltip
        forecast={{
          status: properties.status,
          comment: properties.comment,
          startDate: properties.startDate.toISOString(),
          endDate: properties.endDate?.toISOString(),
          externalSource: properties.externalSource,
          title: properties.title
        }}
        user={user}
        statusColor={textColor}
        showTooltip={!(isMobile && isEditable)}
      >
        <div style={{ position: 'relative', width: '100%', height: '100%' }}>
          <div className={classes.content}>
            {showStatusText && (
              <div className={classes.statusSection}>
                <span className={classes.title}>{properties.title}</span>
                <span className={classes.dateRange}>
                  {formattedDateRange({
                    startDate: properties.startDate.toISOString(),
                    endDate: properties.endDate?.toISOString()
                  })}
                </span>
              </div>
            )}
            {showCommentText && (
              <div className={classes.commentSection}>
                <div className={classes.commentText} style={{ fontSize: commentFontSize }}>
                  {properties.comment}
                </div>
              </div>
            )}
            {showCommentIcon && (
              <div className={classes.commentIcon}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2z"
                    fill="white"
                    fillOpacity="1"
                  />
                </svg>
              </div>
            )}
          </div>

          {/* Resize handles */}
          {isEditable && onMouseDownResize && (
            <>
              <div
                className={classNames(classes.resizeHandle, classes.resizeHandleLeft)}
                style={{ width: itemPixelWidth / 2.2 < 50 ? (itemPixelWidth / 2.2) : 50 }} onMouseDown={(e) => {
                  e.stopPropagation();
                  onMouseDownResize(e, properties._id || '', actualStartDay, actualEndDay, 'start');
                }}
                onTouchStart={onTouchResize ? (e) => {
                  e.stopPropagation();
                  haptic('light');
                  onTouchResize(e.touches[0].clientX, properties._id || '', actualStartDay, actualEndDay, 'start');
                } : undefined}
              >
                <div className={classes.resizeGrip} />
              </div>
              <div
                className={classNames(classes.resizeHandle, classes.resizeHandleRight)}
                style={{ width: itemPixelWidth / 2.2 < 50 ? (itemPixelWidth / 2.2) : 50 }}
                onMouseDown={(e) => {
                  e.stopPropagation();
                  onMouseDownResize(e, properties._id || '', actualStartDay, actualEndDay, 'end');
                }}
                onTouchStart={onTouchResize ? (e) => {
                  e.stopPropagation();
                  haptic('light');
                  onTouchResize(e.touches[0].clientX, properties._id || '', actualStartDay, actualEndDay, 'end');
                } : undefined}
              >
                <div className={classes.resizeGrip} />
              </div>
            </>
          )}
        </div>
      </ForecastTooltip>
    </div>
  );
};

export default memo(CalendarItem);

